/**
 * Pacote 3 · P3-33 a P3-38 (D4 aprovada, critérios 5.10 a 5.12) — base única de valor, custo e margem para Pedidos
 * e Ordens, por linha e por data de corte. Funções puras, iguais no servidor e na tela.
 *
 *  - faturamento acumulado válido no corte: lançamentos com data até o corte, menos os estornados até o corte; cada
 *    lançamento guarda a sua base (valor da NF e custo da revisão da Ordem válida no lançamento);
 *  - carteira no corte: quantidade executável ainda não faturada das Ordens vigentes, na revisão válida no corte;
 *  - valor ainda sem Ordem: quantidade do Pedido (revisão válida no corte) que nenhuma Ordem cobre;
 *  - saldo cancelado sem substituta (magnitude positiva): saldo de Ordem cancelada sem substituta, fora da
 *    retificação, e o Pedido cancelado diretamente (P3-17);
 *  - vendido líquido = faturado + carteira + sem Ordem = original + ajuste assinado da retificação − cancelado.
 * Todas as datas no horário da INTTRIX, no formato AAAA-MM-DDTHH:MM (o corte é o fim do dia AAAA-MM-DD).
 * Valores sem frete e sem abatimento.
 */

export interface Valores {
  q: number;
  v: number;
  c: number;
}
export const ZERO: Valores = { q: 0, v: 0, c: 0 };
const r2 = (x: number) => Math.round(x * 100) / 100;
export const somar = (...xs: Valores[]): Valores => xs.reduce((a, b) => ({ q: a.q + b.q, v: r2(a.v + b.v), c: r2(a.c + b.c) }), ZERO);
export const subtrair = (a: Valores, b: Valores): Valores => ({ q: a.q - b.q, v: r2(a.v - b.v), c: r2(a.c - b.c) });
export const margem = (x: Valores) => r2(x.v - x.c);
/** Percentual agregado = margem agregada / valor agregado; sem base, null (a tela mostra traço). */
export const percentual = (x: Valores) => (Math.abs(x.v) > 0.004 ? (x.v - x.c) / x.v : null);
const de = (q: number, preco: number, custo: number): Valores => ({ q, v: r2(q * preco), c: r2(q * custo) });

export interface LinhaPreco {
  itemId: string;
  quantidade: number;
  precoUnit: number;
  custoUnit: number;
}
export interface LancamentoConc {
  /** data/hora efetiva do lançamento */
  dataHora: string;
  /** data/hora do estorno (null = válido) */
  estornoEm: string | null;
  /** por item: quantidade, valor e custo na base do lançamento */
  itens: { itemId: string; q: number; v: number; c: number }[];
}
export interface OrdemConc {
  numero: string;
  criadaEm: string;
  /** revisões com o período de validade (a atual tem ate = null) */
  revisoes: { de: string; ate: string | null; itens: LinhaPreco[] }[];
  canceladaEm: string | null;
  motivoCancelamento: string | null;
  temSubstituta: boolean;
  lancamentos: LancamentoConc[];
}

const fimDoDia = (corte: string) => (corte.length > 10 ? corte : `${corte}T23:59`);
const ate = (data: string | null, corte: string) => data !== null && data.slice(0, 16) <= fimDoDia(corte);

/** Revisão válida no corte (a primeira, se o corte for anterior a todas). */
export function revisaoEm<T extends { de: string; ate: string | null }>(revisoes: T[], corte: string): T {
  const fim = fimDoDia(corte);
  return revisoes.find((r) => r.de.slice(0, 16) <= fim && (r.ate === null || r.ate.slice(0, 16) > fim)) ?? revisoes[0];
}

/** Faturamento acumulado válido no corte, por item (ou total). */
export function faturadoAte(o: OrdemConc, corte: string, itemId?: string): Valores {
  return somar(...o.lancamentos.filter((f) => ate(f.dataHora, corte) && !ate(f.estornoEm, corte)).flatMap((f) => f.itens.filter((i) => !itemId || i.itemId === itemId).map((i) => ({ q: i.q, v: i.v, c: i.c }))));
}
/** Faturamento do período: lançamentos (+) e estornos (−) com data dentro do período. */
export function faturadoNoPeriodo(o: OrdemConc, inicio: string, fim: string, itemId?: string): Valores {
  const dentro = (d: string | null) => d !== null && d.slice(0, 10) >= inicio && d.slice(0, 10) <= fim;
  const itens = (f: LancamentoConc) => f.itens.filter((i) => !itemId || i.itemId === itemId);
  const entra = somar(...o.lancamentos.filter((f) => dentro(f.dataHora)).flatMap(itens));
  const sai = somar(...o.lancamentos.filter((f) => dentro(f.estornoEm)).flatMap(itens));
  return subtrair(entra, sai);
}
const existe = (o: OrdemConc, corte: string) => o.criadaEm.slice(0, 16) <= fimDoDia(corte);
const canceladaNo = (o: OrdemConc, corte: string) => ate(o.canceladaEm, corte);
/** Saldo que sai por "Retificação" ou com substituta não reduz a venda (critério 5.8: sem dupla subtração). */
const cancelamentoEfetivo = (o: OrdemConc) => !o.temSubstituta && o.motivoCancelamento !== 'Retificação';

/** Carteira no corte (saldo executável não faturado da Ordem vigente), por item ou total. */
export function carteiraEm(o: OrdemConc, corte: string, itemId?: string): Valores {
  if (!existe(o, corte) || canceladaNo(o, corte)) return ZERO;
  const rev = revisaoEm(o.revisoes, corte);
  return somar(...rev.itens.filter((i) => !itemId || i.itemId === itemId).map((i) => de(Math.max(0, i.quantidade - faturadoAte(o, corte, i.itemId).q), i.precoUnit, i.custoUnit)));
}
/** Saldo cancelado sem substituta no corte (magnitude positiva), por item ou total. */
export function canceladoEm(o: OrdemConc, corte: string, itemId?: string): Valores {
  if (!existe(o, corte) || !canceladaNo(o, corte) || !cancelamentoEfetivo(o)) return ZERO;
  const rev = revisaoEm(o.revisoes, o.canceladaEm!);
  return somar(...rev.itens.filter((i) => !itemId || i.itemId === itemId).map((i) => de(Math.max(0, i.quantidade - faturadoAte(o, corte, i.itemId).q), i.precoUnit, i.custoUnit)));
}
/** Quantidade do item coberta pela Ordem no corte (o que sai por substituição ou retificação não fica coberto aqui). */
function cobertura(o: OrdemConc, corte: string, itemId: string) {
  if (!existe(o, corte)) return 0;
  const q = revisaoEm(o.revisoes, corte).itens.filter((i) => i.itemId === itemId).reduce((s, i) => s + i.quantidade, 0);
  if (!canceladaNo(o, corte)) return q;
  return cancelamentoEfetivo(o) ? q : faturadoAte(o, corte, itemId).q;
}

export interface PedidoConc {
  tipo: 'venda' | 'locacao' | 'trade_in';
  fechamento: string;
  canceladoEm: string | null;
  /** revisões do Pedido (r0 primeiro), com as linhas econômicas e o parceiro de cada linha */
  revisoes: { de: string; ate: string | null; linhas: (LinhaPreco & { grupoId: string })[] }[];
  ordens: OrdemConc[];
}
export interface LinhaConciliada {
  itemId: string;
  grupoId: string;
  original: Valores;
  faturado: Valores;
  carteira: Valores;
  semOrdem: Valores;
  cancelado: Valores;
  vendidoLiquido: Valores;
  /** assinado: vendido líquido − original + cancelado */
  ajuste: Valores;
}

/** Conciliação por linha do Pedido no corte (null = Pedido fechado depois do corte). */
export function conciliarPedido(p: PedidoConc, corte: string): LinhaConciliada[] | null {
  if (p.fechamento.slice(0, 10) > corte.slice(0, 10)) return null;
  const r0 = p.revisoes[0];
  const rev = revisaoEm(p.revisoes, corte);
  const cancelado = ate(p.canceladoEm, corte);
  const ids = [...new Set([...r0.linhas.map((l) => l.itemId), ...rev.linhas.map((l) => l.itemId)])];
  return ids.map((itemId) => {
    const l0 = r0.linhas.find((l) => l.itemId === itemId);
    const lr = rev.linhas.find((l) => l.itemId === itemId);
    const original = l0 ? de(l0.quantidade, l0.precoUnit, l0.custoUnit) : ZERO;
    const base = lr ?? l0!;
    const grupoId = base.grupoId;
    if (p.tipo !== 'venda') {
      // Locação e Trade-in: sem OCF; a venda é a revisão válida, e sai inteira só pelo cancelamento do Pedido
      const atual = lr ? de(lr.quantidade, lr.precoUnit, lr.custoUnit) : ZERO;
      const canc = cancelado ? atual : ZERO;
      const vl = cancelado ? ZERO : atual;
      return { itemId, grupoId, original, faturado: ZERO, carteira: ZERO, semOrdem: vl, cancelado: canc, vendidoLiquido: vl, ajuste: somar(subtrair(vl, original), canc) };
    }
    const faturado = somar(...p.ordens.map((o) => faturadoAte(o, corte, itemId)));
    const carteira = somar(...p.ordens.map((o) => carteiraEm(o, corte, itemId)));
    let canc = somar(...p.ordens.map((o) => canceladoEm(o, corte, itemId)));
    const coberto = p.ordens.reduce((s, o) => s + cobertura(o, corte, itemId), 0);
    const restante = Math.max(0, (lr?.quantidade ?? 0) - coberto);
    let semOrdem = ZERO;
    if (restante > 0 && lr) {
      if (cancelado) canc = somar(canc, de(restante, lr.precoUnit, lr.custoUnit));
      else semOrdem = de(restante, lr.precoUnit, lr.custoUnit);
    }
    const vl = somar(faturado, carteira, semOrdem);
    return { itemId, grupoId, original, faturado, carteira, semOrdem, cancelado: canc, vendidoLiquido: vl, ajuste: somar(subtrair(vl, original), canc) };
  });
}

/** Soma das linhas (cards e totais da lista); o filtro de parceiro escolhe as linhas antes de somar. */
export function totalizar(linhas: LinhaConciliada[]) {
  const s = (k: keyof Omit<LinhaConciliada, 'itemId' | 'grupoId'>) => somar(...linhas.map((l) => l[k]));
  return { original: s('original'), faturado: s('faturado'), carteira: s('carteira'), semOrdem: s('semOrdem'), cancelado: s('cancelado'), vendidoLiquido: s('vendidoLiquido'), ajuste: s('ajuste') };
}

/** Ordens no período (cards e lista de OCF): faturado no período, acumulado até o fim, carteira no fim e valor mantido. */
export function apurarOrdem(o: OrdemConc, inicio: string, fim: string) {
  const faturadoAteFim = faturadoAte(o, fim);
  const carteira = carteiraEm(o, fim);
  return { noPeriodo: faturadoNoPeriodo(o, inicio, fim), faturado: faturadoAteFim, carteira, mantido: somar(faturadoAteFim, carteira), cancelado: canceladoEm(o, fim) };
}
